/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { HighResolutionText } from '@appCanvasCommon/text/HighResolutionText';
import { LayerPaths } from '@appPlanApplication/LayerPaths';
import { cssVar } from '@touchplan/css-var';
import { applyDisplayLineTextStyling } from '@appPlanApplication/timeline/TimelineHelpers';
import { AbstractAreaDrawer } from '@appCanvasCommon/core/AbstractAreaDrawer';
import { TextStyle } from 'pixi.js-legacy';
import { FancyDefaultStyles } from '@appCanvasCommon/text/FancyDefaultStyles';
import { FancyTextStyle } from '@appCanvasCommon/interfaces/fancy-text-style.interface';
/**
 * Visual configuration settings for the display line.
 * These settings control the appearance of both the line and its label.
 * The hitArea settings control how much of an area around the visible line can be interacted with.
 * This allows for less precise gestures and interaction with smaller objects (like using a finger to interact with a thin line)
 */
export const displayLineSettings = {
	lineColor: cssVar.grey12,
	lineWidth: 20,
};
export abstract class AbstractDisplayLine extends AbstractAreaDrawer {
	/**
	 * High-resolution text object for the date label
	 * We use a HighResolutionText object instead of FancyText to simplify support across the different renderers
	 * FancyText only supports WebGL, whereas HighResolutionText supports both.
	 * @protected
	 */
	protected _label: HighResolutionText;
	constructor(parentApplication: AbstractApplication, layerPath: LayerPaths) {
		super(parentApplication, layerPath);
		// We create a new FancyTextStyle here because every line instance would otherwise share the same FancyTextStyle
		// so changing the style for one line (line text color for active line), would change the color for every line.
		this._label = new HighResolutionText(``, new FancyTextStyle(FancyDefaultStyles.DEFAULT_STYLE));
		parentApplication.layerManager.addChildToLayer(layerPath, this._label);
	}
	/**
	 * Draws the line visualization.
	 * Creates a vertical line and positions the rotated date label.
	 * The AbstractDisplayLine will always draw for the entire screen height
	 */
	public draw(): void {
		super.draw();
		const height: number = this.parentApplication.camera.getScreen().height;
		this._graphics.beginFill(displayLineSettings.lineColor);
		this._graphics.drawRect(0, 0, displayLineSettings.lineWidth, height);
		this._graphics.endFill();
		applyDisplayLineTextStyling(this._label);
	}

	public hideLabel(): void {
		this._label.visible = false;
	}

	public showLabel(): void {
		this._label.visible = true;
	}
}
